import { useMemo } from 'react';
import type { PlaylistItem } from '../types/PlaylistItem';
import type { MediaQueueItem } from '../types/MediaQueueItem';

interface UseMediaQueueReturn {
  queue: MediaQueueItem[];
  currentMedia: MediaQueueItem | null;
  nextMedia: MediaQueueItem | null;
  previousMedia: MediaQueueItem | null;
  audioItems: MediaQueueItem[];
  visualItems: MediaQueueItem[];
  hasAudio: boolean;
  hasVisual: boolean;
}

const VALID_TYPES = new Set(['audio', 'image', 'video']);

function normalizeItem(item: PlaylistItem): MediaQueueItem {
  const type = item.type as 'audio' | 'image' | 'video';

  if (type === 'audio') {
    return {
      id: item.id,
      type: 'audio',
      title: item.title,
      artist: item.artist,
      media_url: item.media_url!,
      thumbnail_url: item.thumbnail_url,
      duration_seconds: item.duration_seconds ?? 180,
      display_duration_seconds: item.display_duration_seconds ?? 0,
      isVisual: false,
      isAudio: true,
    };
  }

  if (type === 'image') {
    return {
      id: item.id,
      type: 'image',
      title: item.title,
      artist: item.artist,
      media_url: item.media_url!,
      thumbnail_url: item.thumbnail_url,
      duration_seconds: 0,
      display_duration_seconds: item.display_duration_seconds ?? 8,
      isVisual: true,
      isAudio: false,
    };
  }

  return {
    id: item.id,
    type: 'video',
    title: item.title,
    artist: item.artist,
    media_url: item.media_url!,
    thumbnail_url: item.thumbnail_url,
    duration_seconds: item.duration_seconds ?? 30,
    display_duration_seconds: 0,
    isVisual: true,
    isAudio: false,
  };
}

export function useMediaQueue(
  playlist: PlaylistItem[],
  currentIndex: number,
): UseMediaQueueReturn {
  const queue = useMemo(() => {
    return playlist
      .filter((item) => {
        if (!item.id) return false;
        if (!item.type || !VALID_TYPES.has(item.type)) return false;
        if (!item.media_url) return false;
        return true;
      })
      .map(normalizeItem);
  }, [playlist]);

  const currentMedia =
    queue.length > 0 && currentIndex >= 0 && currentIndex < queue.length
      ? queue[currentIndex]
      : null;

  const nextMedia =
    queue.length > 1 ? queue[(currentIndex + 1) % queue.length] : null;

  const previousMedia =
    queue.length > 1
      ? queue[(currentIndex - 1 + queue.length) % queue.length]
      : null;

  const audioItems = useMemo(() => queue.filter((i) => i.isAudio), [queue]);
  const visualItems = useMemo(() => queue.filter((i) => i.isVisual), [queue]);
  const hasAudio = audioItems.length > 0;
  const hasVisual = visualItems.length > 0;

  return {
    queue,
    currentMedia,
    nextMedia,
    previousMedia,
    audioItems,
    visualItems,
    hasAudio,
    hasVisual,
  };
}